/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Enterprise is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Enterprise is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Enterprise, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Enterprise:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Follows a 'code splitting' architecture.
 *  - Is completely custom built (no JS libraries other than instant.page)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Enterprise broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
 *  4.  on:line-item:change
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - product: the product object (includes a list of all variants)
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart. To listen for this, see the
 * on:line-item:change event.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the variant was added
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:line-item:change
 * -------------------------------------------------------------------------------------------------
 * Fires when the quantity of an item already in the cart is updated. Note, if the 'newQuantity' is
 * 0, this indicates the item was removed from that cart.
 *
 * Note, when adding a variant to the cart - this event will fire if that variant is already in the
 * cart (i.e. the quantity is incremented). In this situation, 'on:cart:add' will not fire.
 *
 * How to listen:
 * document.addEventListener('on:line-item:change', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - cart: the new cart object after the quantity change was completed
 *   - variantId: id of the variant that was just updated
 *   - newQuantity: new quantity of the line item
 *   - oldQuantity: old quantity of the line item
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes. See the 'Media Queries' section in this file
 * for more.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Enterprise utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 * =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Enterprise only has one third-party dependency: instant.page (https://instant.page/).
 *
 * It's included locally and is only active if it has been enabled in
 * 'Theme Settings > Advanced > Preload links on hover'.
 *
 * Instant.page is a JavaScript library that speeds up page loads by preloading links as soon as the
 * customer hovers over them.
 *
 *
 *
 * =================================================================================================
 * Code Splitting
 * =================================================================================================
 * We followed the ‘code splitting’ technique when building Enterprise.
 *
 * Code splitting consists in writing JavaScript (and CSS)in a modularized way within typically
 * small, more manageable files that can be loaded on-demand, as needed. The idea is to improve the
 * performance of our theme by reducing the amount of code that needs to be loaded upfront.
 *
 * If the customer is visiting a specific page of the theme that requires certain JavaScript
 * functionality, only the code needed for that page will be loaded, rather than one large
 * JavaScript file containing largely unused code. For example, the file media-gallery.js will
 * only be loaded if there is a media gallery on the page.
 *
 * Shopify uses HTTP/2, which is the newer version of the HTTP protocol used to deliver web content.
 * HTTP/2 supports multiplexing, which means that multiple requests can be sent over a single
 * connection at the same time - meaning multiple JS files are essentially served at the speed of a
 * single file.
 *
 * The only JS file which is served on every page in Enterprise is 'main.js'. Main.js contains
 * utility JS which is likely to be needed by many scripts. This is outlined more in the next
 * section.
 *
 *
 *
 * =================================================================================================
 * Utilities
 * =================================================================================================
 * Enterprise provides a few utility utilities, contained in main.js. Some of the key ones are
 * outlined below. See main.js for more.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 1) Lazy Loading
 * -------------------------------------------------------------------------------------------------
 * Lazy loading is a technique for delaying the loading of certain elements until they are needed,
 * which can help improve page load times.
 *
 * We use three functions used for lazy loading images and scripts in our theme:
 *
 *  - setImageSources function - copies the data-src and data-srcset attributes of lazy loaded
 *    images to their src and srcset attributes.
 *  - initLazyImages function - uses the IntersectionObserver API to lazy load images when needed.
 *  - initLazyScript function - only loads a script when a specific element is scrolled into view.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) Cookies
 * -------------------------------------------------------------------------------------------------
 * Cookies are small pieces of data that can be stored on a user's computer. They can be useful for
 * tracking user activity, remembering user preferences or other similar purposes.
 *
 * We use three functions to manage cookies:
 *
 *  - setCookie function - sets a cookie with a given name, value and number of days until it
 *  should expire.
 *  - getCookie function - takes in the name of a cookie to retrieve its value.
 *  - deleteCookie function - takes in the name of a cookie to delete it.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) Media Queries
 * -------------------------------------------------------------------------------------------------
 * The theme creates a theme.mediaMatches object (in theme.liquid) for several key screen sizes
 * specified in our theme, and adds listeners for each media query.
 *
 * These are:
 *
 * mediaQueries: {
 *  sm: '(min-width: 600px)',
 *  md: '(min-width: 769px)',
 *  lg: '(min-width: 1024px)',
 *  xl: '(min-width: 1280px)',
 *  xxl: '(min-width: 1536px)',
 *  portrait: '(orientation: portrait)'
 * }
 *
 * If a breakpoint is crossed, the mediaMatches values are updated and an 'on:breakpoint-change'
 * event is dispatched.
 *
 * You can request the entire theme.mediaMatches object to check which media queries are currently
 * matched. In this case, the returned data will be an object with the names of the media queries as
 * keys, and boolean values indicating whether they are currently matched or not.
 *
 * Example:
 *
 * {
 *  sm: true,
 *  md: true,
 *  lg: true,
 *  xl: true,
 *  xxl: false,
 *  portrait: false
 * }
 *
 * You can reference a specific media query to check if it's currently matched by using:
 * theme.mediaMatches.lg.
 *
 * To check if you're on mobile you can use:
 * !theme.mediaMatches.md
 *
 * If you want to perform some action when the breakpoint changes, you can listen for the
 * breakpoint-change event on the window object.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  // your code here
 * });
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

/*== Show back to top button en scroll ==*/
const btnScrollToTop = document.querySelector(".back-to-top");
window.addEventListener('scroll', e => {
  btnScrollToTop.style.display = window.scrollY > 300 ? 'flex' : 'none';
});

// Fonction scroll
function smoothScroll(target) {
  const element = document.getElementById(target);
  if (element) {
    let scrolloffsetTop = element.offsetTop;
    if (window.matchMedia('(min-width: 1080px)').matches ) {
      scrolloffsetTop -= 80;
    } else {
      scrolloffsetTop -= 120;
    }
    window.scrollTo({
      top: scrolloffsetTop,
      behavior: 'smooth'
    });
  }
}
document.addEventListener('DOMContentLoaded', function() {
  const anchors = document.querySelectorAll('a[href^="#"]:not(.back-to-top)');
  anchors.forEach(function(anchor) {
    anchor.addEventListener('click', function(event) {
      event.preventDefault();
      const target = anchor.getAttribute('href').substring(1);
      smoothScroll(target);
    });
  });
});

/*== Set height of media background in product page ==*/
function outerHeightWithMargin(el) {
  if (!el) return 0;
  const style = getComputedStyle(el);
  return el.offsetHeight + parseFloat(style.marginTop) + parseFloat(style.marginBottom);
}

function getMediaHeight() {
  const mediaGalleryHeight = outerHeightWithMargin(document.querySelector('.media-gallery__viewer'));
  const breadcrumbsHeight = outerHeightWithMargin(document.querySelector('.breadcrumbs'));
  return mediaGalleryHeight + breadcrumbsHeight;
}

function setBgHalfHeight() {
  const bgHalf = document.querySelector('.bg-half');
  if (bgHalf) {
    bgHalf.style.height = getMediaHeight() + 'px';
  }
}

setBgHalfHeight();
window.addEventListener('on:debounced-resize', setBgHalfHeight);

/*== Accordion menu level 4 ==*/
const lev4 = document.querySelectorAll(".grandchild-nav__item-container");
const lev4Toggle = document.querySelectorAll(".toggle-menu");
const lev4First = document.querySelectorAll(".main-nav__grandchild li:nth-child(3)");

window.addEventListener("load", function() {
  lev4.forEach(item => {
    item.classList.remove("is-open");
  });
  lev4Toggle.forEach((item) => {
    item.addEventListener("click", () => {
      let parent = item.closest("li");
      parent.classList.toggle("is-open");
    });
  });
  lev4First.forEach(item => {
    item.classList.add("is-open");
  });
});

/*== Mobile menu under 1280px (xl) ==*/
if(!theme.mediaMatches.xl) {
  initMenuMobile();
}
window.addEventListener('on:debounced-resize', (event) => {
  initMenuMobile();
});

function initMenuMobile() {
  const menuChildren = document.querySelectorAll('.child-nav-container > details');
  menuChildren.forEach((el) => {
    if (window.matchMedia('(max-width: 1280px)').matches) {
      el.removeAttribute('open');
    } else {
      el.setAttribute('open', '');
    }
  });
  const menuChildrenSummary = document.querySelectorAll('.child-nav__item--toggle');
  menuChildrenSummary.forEach((item) => {
    if (window.matchMedia('(max-width: 1280px)').matches) {
      item.classList.remove('is-visible');
    } else {
      item.classList.add('is-visible');
    }
    item.addEventListener('click', toggleVisible(item));
  });
}
function toggleVisible(el) {
  el.classList.toggle('is-visible');
}

/*== Collection Breadcrumbs ==*/
const breadcrumbsNav = document.querySelector("#nav-breadcrumbs");
if (breadcrumbsNav) {
  const breadcrumbsCollection = document.querySelector("#nav-breadcrumbs__collection");
  const breadcrumbs = breadcrumbsNav.innerHTML;
  window.addEventListener('DOMContentLoaded', setBreadcrumbs);
  function setBreadcrumbs() {
    if (breadcrumbsCollection) {
      breadcrumbsNav.innerHTML = '';
      breadcrumbsCollection.innerHTML = breadcrumbs;
    } else {
      breadcrumbsNav.innerHTML = breadcrumbs;
    }
  }
}

/*== Open description & references disclosure in product page ==*/
const linkToDescription = document.querySelector(".link-to-description");
const sectionDescription = document.querySelector("#description > details");
const linkToReferences = document.querySelector(".link-to-references");
const sectionReferences = document.querySelector("#references > details");
const linkToComposition = document.querySelector(".link-to-composition");

openDisclosureCustom(linkToDescription, sectionDescription);
openDisclosureCustom(linkToReferences, sectionReferences);
openDisclosureCustom(linkToComposition, sectionReferences);

function openDisclosureCustom(trigger, el) {
  if(trigger) {
    trigger.addEventListener('click', function() {
      el.setAttribute("open", "");
      let yOffset = 0;
      if(!theme.mediaMatches.md) {
        yOffset = -100;
      } else {
        yOffset = -50;
      }
      let y = el.getBoundingClientRect().top + window.scrollY + yOffset;
      if(theme.mediaMatches.md & !theme.mediaMatches.xl) {
        window.scrollTo({top: y});
      } else {
        window.scrollTo({top: y, behavior: 'smooth'});
      }
    });
  }
}

/*======= Product bundle components pager =======*/
const listBundleContainer = document.querySelector(".list-bundle__container")
if(listBundleContainer) {
  const listBundle = listBundleContainer.querySelector(".list-bundle")
  const listBundleElements = listBundle.querySelectorAll("li")
  const listBundleArray = Array.from(listBundleElements)
  const pagination = listBundleContainer.querySelector(".pagination")

  const total = listBundleArray.length
  const itemPerPage = 10
  const pages = Math.ceil(total/itemPerPage)
  let currentPage = 1
  let prev = 0
  let next = 2
  const prevText = pagination.getAttribute("data-text-prev")
  const nextText = pagination.getAttribute("data-text-next")
  document.addEventListener("load", pageNum(currentPage))

  function pageNum(num) {
    listBundle.innerHTML = ''
    currentPage = num
    prev = currentPage - 1
    next = currentPage + 1
    const slice = listBundleArray.slice(itemPerPage * (currentPage - 1), itemPerPage * currentPage)

    for(let i=0; i<slice.length; i++) {
      listBundle.append(slice[i])
    }
    pagination.innerHTML = `
    <li onclick="pageNum(${prev})" class="pagination__item pagination__item-fitst pagination__item--arrow${currentPage == 1 ? " disabled" : ""}">
      <span class="pagination__arrow pagination__arrow--prev hover:lighten flex text-current items-center has-ltr-icon" role="link">
        <span class="pagination__arrow-icon" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z"></path></svg></span>
        <span class="pagination__prev-label mis-2">${prevText}</span>
      </span>
    </li>`
    if (!theme.mediaMatches.md) {
      pagination.innerHTML += `
      <li class="pagination__item text-center md:hidden font-bold">
        Page ${currentPage} / ${pages}
      </li>`
    }
    for(let i = 1; i<=pages; i++) {
      if(i == currentPage) {
        pagination.innerHTML += `
        <li class="hidden md:block">
          <span class="pagination__page-current font-bold block leading-none">${i}</span>        
        </li>`
      } else {
        pagination.innerHTML += `
        <li onclick="pageNum(${i})" class="hidden md:block">
          <span class="pagination__page-link text-current block leading-none" role="link">${i}</span>        
        </li>`
      } 
    }
    pagination.innerHTML += `
    <li onclick="pageNum(${next})" class="pagination__item pagination__item--arrow${currentPage == pages ? " disabled" : ""}">
      <span class="pagination__arrow pagination__arrow--next hover:lighten flex justify-end text-current items-center has-ltr-icon js-pagination-load-more" role="link">
        <span class="pagination__next-label text-end">${nextText}</span>
        <span class="pagination__arrow-icon" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" stroke="currentColor" stroke-width="1.5" fill="none"></path></svg></span>
      </span>
    </li>`
  }
}